import { AmaxPayShortLogo } from '@approvalmax/ui';
import { arrayHelpers, compareHelpers } from '@approvalmax/utils';
import { domain } from 'modules/data';
import { useMemo } from 'react';
import bemFactory from 'react-bem-factory';
import {
    DomainEntitiesCompanyRole,
    useGetCompany,
    useGetCompanyMe,
    useGetCompanyWorkflows,
} from 'shared/data/webApp/v2';
import { getPath, Path } from 'urlBuilder';

import DisabledWorkflowPanel from '../DisabledWorkflowPanel/DisabledWorkflowPanel';
import { sortedIntegrationCodes } from './WorkflowsMenu.constants';
import { getWorkflowsMeta } from './WorkflowsMenu.helpers';
import { messages, workflowsMessages } from './WorkflowsMenu.messages';

const qa = bemFactory.qa('requests-section');

export const useWorkflowsLinkItems = (companyId: string) => {
    const { data: me } = useGetCompanyMe({ path: { companyId } });
    const { data: company } = useGetCompany({ path: { companyId } });

    const { data: workflowsData } = useGetCompanyWorkflows({ path: { companyId } });

    const isAuditor = me?.role === DomainEntitiesCompanyRole.Auditor;
    const isManager = me?.role === DomainEntitiesCompanyRole.Manager;
    const isWorkflowManager = me?.role === DomainEntitiesCompanyRole.WorkflowManager;

    const canSeeDisabledWorkflows = isAuditor || isManager || isWorkflowManager;
    const canConfigureDisabledWorkflows = isManager || isWorkflowManager;

    const workflows = useMemo(() => {
        if (!company || !workflowsData) return [];

        const workflows = workflowsData.items;

        const workflowsMeta = getWorkflowsMeta({
            company,
            isAirwallexIntegrationConnected: company.isAirwallexIntegrationConnected,
            isXeroPoGrnEnabled: workflows.find((workflow) => workflow.integrationCode === domain.IntegrationCode.XeroPo)
                ?.isGrnEnabled,
            isQboPoGrnEnabled: workflows.find(
                (workflow) => workflow.integrationCode === domain.IntegrationCode.QBooksPo
            )?.isGrnEnabled,
        });

        return workflows.map((workflow) => {
            const workflowMeta = workflowsMeta[(workflow.integrationCode as domain.IntegrationCode) || 'Standalone'];

            const workflowName = workflowMeta.name || workflow.name || workflowsMessages.unnamedName;
            const workflowDisabled = !workflow.isEnabled && canConfigureDisabledWorkflows && !company.isReadonly;
            const workflowAvailable =
                (workflowMeta.available ?? true) && (workflow.isEnabled || canSeeDisabledWorkflows);

            const enrichedSubItems = workflowMeta.subItems.map((subItem) => ({
                ...subItem,
                title: `${workflowName}: ${subItem.name}`,
                to: getPath(Path.requestTemplateList, workflow.companyId, workflow.id, subItem.id),
                dataQa: qa('navigationItem'),
            }));

            const workflowHasAddButton =
                (workflowMeta.hasAddButton ?? true) &&
                !company?.isReadonly &&
                workflow.isEnabledForRequestCreation &&
                workflow.isEnabled &&
                (!workflow.integrationCode || company?.isIntegrationEnabled);
            const addButton =
                workflowHasAddButton && company
                    ? {
                          title: messages.addButtonTitle,
                          to: getPath(Path.newRequest, workflow.id, company.id),
                      }
                    : undefined;

            const startIcon =
                workflow.integrationCode === domain.IntegrationCode.XeroAmaxPayBatchPayment ? (
                    <AmaxPayShortLogo color='midnight100' width={32} height={12} />
                ) : undefined;

            return {
                ...workflow,
                ...workflowMeta,
                name: workflowName,
                title: workflowName,
                subItems: enrichedSubItems,
                available: workflowAvailable,
                id: workflow.integrationCode || workflow.id,
                workflowId: workflow.id,
                disabled: workflowDisabled,
                disabledFullWidth: Boolean(startIcon),
                disabledComponent: company ? (
                    <DisabledWorkflowPanel company={company} workflowId={workflow.id} startIcon={startIcon} />
                ) : null,
                addButton,
                dataQa: qa(),
                dataQaName: workflow.integrationCode || workflowName,
            };
        }, []);
    }, [canConfigureDisabledWorkflows, canSeeDisabledWorkflows, company, workflowsData]);

    /**
     * Sort standalone workflows by name and sort workflows with integration relative to order in the meta
     */
    return useMemo(() => {
        if (!company) return [];

        const sortedIntegrationWorkflows: typeof workflows = [];

        sortedIntegrationWorkflows.length = workflows.length;

        const sortedStandaloneWorkflows = arrayHelpers.arraySort(
            workflows.filter((workflow) => {
                if (workflow.integrationCode) {
                    const index = sortedIntegrationCodes.indexOf(workflow.integrationCode as domain.IntegrationCode);

                    sortedIntegrationWorkflows[index] = workflow;
                }

                return !workflow.integrationCode;
            }),
            compareHelpers.comparatorFor(compareHelpers.stringComparator2Asc, 'name')
        );

        return sortedIntegrationWorkflows.filter(Boolean).concat(sortedStandaloneWorkflows);
    }, [company, workflows]);
};
